import * as RadixSelectPrimitive from '@radix-ui/react-select';
import { ChevronDown, Check } from 'lucide-react';
import { forwardRef, type ReactNode } from 'react';
import { cn } from './cn';

export interface SelectOption {
  value: string;
  label: string;
}

export interface RadixSelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<SelectOption>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

const EMPTY_SENTINEL = '__radix_empty__';
const toSentinel = (v: string) => (v === '' ? EMPTY_SENTINEL : v);
const fromSentinel = (v: string) => (v === EMPTY_SENTINEL ? '' : v);

export const RadixSelect = forwardRef<HTMLButtonElement, RadixSelectProps>(
  (
    {
      id,
      value,
      onChange,
      options,
      placeholder = '선택',
      disabled = false,
      className,
      'aria-label': ariaLabel,
      'data-testid': testId,
    },
    ref,
  ) => {
    const selected = options.find((o) => o.value === value);
    return (
      <RadixSelectPrimitive.Root
        value={toSentinel(value)}
        onValueChange={(v) => onChange(fromSentinel(v))}
        disabled={disabled}
      >
        <RadixSelectPrimitive.Trigger
          ref={ref}
          id={id}
          aria-label={ariaLabel}
          data-testid={testId}
          className={cn(
            'flex h-8 w-full items-center justify-between rounded-md border px-3 text-[13px]',
            'transition-colors duration-[var(--motion-fast)]',
            'focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'data-[placeholder]:text-[color:var(--content-muted)]',
            className,
          )}
          style={{
            background: 'var(--surface-0)',
            color: 'var(--content-strong)',
            borderColor: 'var(--divider-strong)',
          }}
        >
          <RadixSelectPrimitive.Value placeholder={placeholder} asChild>
            <span className="min-w-0 flex-1 truncate text-left">
              {selected?.label ?? placeholder}
            </span>
          </RadixSelectPrimitive.Value>
          <RadixSelectPrimitive.Icon asChild>
            <ChevronDown
              className="ml-2 shrink-0 opacity-60"
              style={{ width: '16px', height: '16px' }}
            />
          </RadixSelectPrimitive.Icon>
        </RadixSelectPrimitive.Trigger>

        <RadixSelectPrimitive.Portal>
          <RadixSelectPrimitive.Content
            className="z-50 min-w-[var(--radix-select-trigger-width)] max-w-[min(420px,calc(100vw-2rem))] overflow-hidden rounded-md border text-sm shadow-[var(--elevation-2)]"
            style={{
              background: 'var(--surface-raised)',
              borderColor: 'var(--divider-strong)',
            }}
            position="popper"
            sideOffset={4}
            collisionPadding={8}
          >
            <RadixSelectPrimitive.Viewport className="max-h-[min(320px,60vh)] overflow-y-auto p-1">
              {options.map((opt) => (
                <SelectItem key={opt.value || EMPTY_SENTINEL} value={toSentinel(opt.value)}>
                  {opt.label}
                </SelectItem>
              ))}
            </RadixSelectPrimitive.Viewport>
          </RadixSelectPrimitive.Content>
        </RadixSelectPrimitive.Portal>
      </RadixSelectPrimitive.Root>
    );
  },
);
RadixSelect.displayName = 'RadixSelect';

interface SelectItemProps {
  value: string;
  children: ReactNode;
}

const SelectItem = ({ value, children }: SelectItemProps) => (
  <RadixSelectPrimitive.Item
    value={value}
    className={cn(
      'relative flex cursor-pointer select-none items-center rounded-sm px-3 py-1.5 pr-8 text-[13px]',
      'outline-none transition-colors duration-[var(--motion-fast)]',
      'data-[highlighted]:bg-[var(--surface-1)]',
      'data-[state=checked]:font-medium',
    )}
    style={{ color: 'var(--content-strong)' }}
  >
    <RadixSelectPrimitive.ItemText asChild>
      <span className="block truncate">{children}</span>
    </RadixSelectPrimitive.ItemText>
    <RadixSelectPrimitive.ItemIndicator className="absolute right-2 flex items-center justify-center">
      <Check style={{ width: '14px', height: '14px', color: 'var(--content-strong)' }} />
    </RadixSelectPrimitive.ItemIndicator>
  </RadixSelectPrimitive.Item>
);
